import React, { useMemo } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';

import type { StoredStandard } from '@/shared/types/customStandard';
import ClampedTextTooltip from '@/shared/components/ClampedTextTooltip';

export type StandardsCoverageColumn = { id: string; label: string };

export type StandardsCoverageMatrixProps = {
  /** Standards to visualize as rows — already resolved, sorted by caller */
  rows: StoredStandard[];
  columns: StandardsCoverageColumn[];
  /** Map of columnId -> standards included in that column */
  coverageByColumn: Record<string, StoredStandard[]>;
  title?: string;
  subtitle?: string;
  dense?: boolean;
  onColumnClick?: (columnId: string) => void;
};

export const StandardsCoverageMatrix: React.FC<StandardsCoverageMatrixProps> = ({
  rows,
  columns,
  coverageByColumn,
  title = 'Standards Coverage',
  subtitle,
  dense = false,
}) => {
  const standardColumnWidth = dense ? 280 : 320;
  const coverageColumnWidth = dense ? 84 : 96;
  const tableMinWidth = standardColumnWidth + columns.length * coverageColumnWidth;

  const coverageSets = useMemo(() => {
    const out: Record<string, Set<string>> = {};
    for (const col of columns) {
      out[col.id] = new Set((coverageByColumn[col.id] ?? []).map((s) => s.id));
    }
    return out;
  }, [columns, coverageByColumn]);

  if (!rows.length) {
    return (
      <Card>
        {(title || subtitle) && (
          <CardHeader
            title={title}
            subheader={subtitle}
            sx={{
              py: 1,
              '&& .MuiCardHeader-title': {
                fontSize: '1.5rem',
                lineHeight: 1.3,
              },
              '&& .MuiCardHeader-subheader': {
                fontSize: '0.75rem',
                lineHeight: 1.3,
              },
            }}
          />
        )}
        <CardContent sx={{ pt: 0 }}>
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            No standards to visualize yet.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      {(title || subtitle) && (
        <CardHeader
          title={title}
          subheader={subtitle}
          sx={{
            py: 1,
            '&& .MuiCardHeader-title': {
              fontSize: '1.5rem',
              lineHeight: 1.3,
            },
            '&& .MuiCardHeader-subheader': {
              fontSize: '0.75rem',
              lineHeight: 1.3,
            },
          }}
        />
      )}
      <CardContent sx={{ pt: 0 }}>
        <TableContainer
          sx={{
            maxWidth: '100%',
            overflowX: 'auto',
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
          }}
        >
          <Table
            size={dense ? 'small' : 'medium'}
            stickyHeader
            sx={{
              tableLayout: 'fixed',
              minWidth: tableMinWidth,
              '& .MuiTableCell-root': {
                borderColor: 'divider',
              },
              '& .MuiTableCell-head': {
                bgcolor: 'background.default',
                color: 'text.secondary',
                fontSize: '0.75rem',
                fontWeight: 700,
                lineHeight: 1.25,
              },
              '& .MuiTableRow-root:last-of-type .MuiTableCell-body': {
                borderBottom: 0,
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    width: standardColumnWidth,
                    minWidth: standardColumnWidth,
                    maxWidth: standardColumnWidth,
                    textTransform: 'uppercase',
                    letterSpacing: 0,
                  }}
                >
                  Standard
                </TableCell>
                {columns.map((c) => (
                  <TableCell
                    key={c.id}
                    align="center"
                    sx={{
                      width: coverageColumnWidth,
                      minWidth: coverageColumnWidth,
                      maxWidth: coverageColumnWidth,
                      px: dense ? 0.75 : 1,
                    }}
                  >
                    <Tooltip title={c.label} arrow enterDelay={700}>
                      <Typography
                        variant="caption"
                        component="span"
                        sx={{
                          display: '-webkit-box',
                          WebkitBoxOrient: 'vertical',
                          WebkitLineClamp: 2,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'normal',
                          overflowWrap: 'anywhere',
                          lineHeight: 1.25,
                        }}
                      >
                        {c.label}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((std) => (
                <TableRow key={std.id} hover>
                  <TableCell
                    sx={{
                      width: standardColumnWidth,
                      minWidth: standardColumnWidth,
                      maxWidth: standardColumnWidth,
                    }}
                  >
                    <ClampedTextTooltip
                      text={std.code ? `${std.code} — ${std.title}` : std.title}
                      lineClamp={2}
                      variant="body2"
                      tooltipPlacement="top-start"
                      sx={{ lineHeight: 1.35 }}
                    />
                  </TableCell>
                  {columns.map((c) => {
                    const covered = coverageSets[c.id]?.has(std.id) ?? false;
                    return (
                      <TableCell
                        key={c.id}
                        align="center"
                        sx={{
                          width: coverageColumnWidth,
                          minWidth: coverageColumnWidth,
                          maxWidth: coverageColumnWidth,
                          px: dense ? 0.5 : 1,
                        }}
                      >
                        {covered ? (
                          <Tooltip title="Included" arrow>
                            <CheckCircleIcon
                              color="success"
                              fontSize={dense ? 'small' : 'medium'}
                            />
                          </Tooltip>
                        ) : (
                          <Tooltip title="Not included" arrow>
                            <RemoveCircleOutlineIcon
                              color="disabled"
                              fontSize={dense ? 'small' : 'medium'}
                            />
                          </Tooltip>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

export default StandardsCoverageMatrix;
